import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, List, Modal } from "semantic-ui-react";
import { DatePicker } from "../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import { addonOptionsFields, EntityType } from "../../../components/shared/constants";
import moment from "moment";
import { handleStartEndChange, filterDateRangesByDateRange } from "../../../components/shared/utils";
import { PriceListContext } from "../context";
import { AddonEditingTable } from "./AddonEditingTable"
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { onWarning } from "../../../components/shared/messages";
import { useConfirm } from "../../../hooks/useConfirm";
import PricingElementPanel from "../../../components/shared/PricingElementPanel";
import { RPEsRearangeDateRanges, insertCopiedPricing, areAllStartEndDatesValid } from "./pricingUtils";
import { PriceElementsList } from "./PriceElementsList";

export const PerPersonAddonPopup = ({ open, onClose, EntityId, RatePricingTypeId, updateRPEs, Packages: ratePricingElementsPackages,
  rateElementName, onDelete, onPricingSave }) => {
  const message = useMessageResources();
  const confirm = useConfirm();
  const currentHotel = useCurrentHotel();
  const text = useTextResources();
  const { state } = useContext(PriceListContext);
  const [pricingElements, setPricingElements] = useState([]);
  const [fromFilterDate, setFromFilterDate] = useState();
  const [toFilterDate, setToFilterDate] = useState();
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().add(1, "day").toDate());
  const [selectedDays, setSelectedDays] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const priceToUpdate = state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";

  useEffect(() => {
    if (open && EntityId && state.RatePricingElements?.length > 0) {
      (async () => {
        //find all RatePricingElements that are mapped to this addon
        const pricingElements = state.RatePricingElements?.filter(x =>
          x.EntityTypeId === EntityType.AddOn &&
          x.EntityId === EntityId &&
          Boolean(x.RatePricingElementsPackages?.length) === Boolean(ratePricingElementsPackages?.length) &&
          x.IsActive && !x.IsDummy);
        const pricingElementsFormatted = pricingElements.map(x => {
          let fittingHotelAddon = x.HotelAddons?.find(x => !x.ExceptionDate);
          return {
            ...x,
            StartDate: x.StartDate,
            EndDate: x.EndDate,
            HotelAddons: fittingHotelAddon ? [fittingHotelAddon] : [{
              RatePricingElementId: x.Id,
              RateId: state.Id,
              IsActive: true,
              CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
              HotelAddonsPrices: [],
              StartDate: x.StartDate,
              EndDate: x.EndDate,
              HotelId: state.HotelId,
              ChainAddonId: EntityId
            }]
          }
        })
        setPricingElements(JSON.parse(JSON.stringify(pricingElementsFormatted.sort((a, b) => a.StartDate < b.StartDate ? -1 : 1))));
      })();
    } else {
      setPricingElements([]);
      setIsDirty(false);
    }
  }, [state.RatePricingElements, EntityId, open]);

  const isValidBeforeSave = async () => {
    if (pricingElements.some(x => x.isEdit)) {
      onWarning(message("ApproveOpenPricingsBeforeSave"));
      return false;
    }

    if (!areAllStartEndDatesValid(pricingElements)) {
      onWarning(message("DatesIsMandatoryField"));
      return false;
    }

    return true;
  }

  const onSave = async (isClose) => {
    const rpes = pricingElements.map(rpe => ({ 
      ...rpe, 
      HotelAddons: rpe.HotelAddons.map(ha => ({
        ...ha, 
        StartDate: rpe.StartDate, 
        EndDate: rpe.EndDate, 
        IsActive: rpe.IsActive, 
        ChainAddonId: EntityId 
      })) 
    }));
    updateRPEs({ rpes, isClose, setIsDirty, validate: isValidBeforeSave, setPricings: setPricingElements });
  };

  const memoDatesForWeekComponent = useMemo(
    () => (startDate && endDate ? [{ startDate, endDate }] : []),
    [startDate, endDate]
  );

  const getHotelAddonOptions = (hotelAddon) => {
    let newOptions = {};
    for (let field of addonOptionsFields)
      newOptions[field] = hotelAddon[field];
    return newOptions;
  }

  const addPricingElement = () => {
    let fittingHotelAddon = pricingElements.filter(x => !x.IsDummy)?.[0]
      ?.HotelAddons?.find(x => !x.ExceptionDate) || {};
    const newPricingElement = {
      Id: 0,
      TempId: `newPricingTempId-${crypto.randomUUID()}`,
      StartDate: startDate,
      EndDate: endDate,
      Weekdays: selectedDays,
      RatePricingTypeId: RatePricingTypeId,
      RateId: state.Id,
      HotelAddons: [{
        ...getHotelAddonOptions(fittingHotelAddon),
        Id: 0,
        RatePricingElementId: 0,
        RateId: state.Id,
        IsActive: true,
        CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
        HotelAddonsPrices: [],
        StartDate: startDate,
        EndDate: endDate,
        HotelId: state.HotelId,
        ChainAddonId: EntityId,
      }],
      RatePricingElementsPackages: ratePricingElementsPackages?.map(pack => ({ ...pack, Id: 0 })),
      EntityId,
      EntityTypeId: EntityType.AddOn,
      IsNew: true,
      IsActive: true
    }

    const newPricings = RPEsRearangeDateRanges(newPricingElement, pricingElements, state.BaseRateId);
    setPricingElements(newPricings)

    setIsDirty(true);
  };

  const onCopy = (oldPricing, newPricing) => {
    const rpes = insertCopiedPricing(pricingElements, oldPricing, newPricing);
    setPricingElements(rpes);
    setIsDirty(true)
  }

  const onPricingSaveClicked = (pricing) => {
    onPricingSave({ rpe: pricing, rpes: pricingElements, setPricings: setPricingElements, setIsDirty });
  }

  const changeHotelAddonPrice = (rpe, priceType, fieldname, value) => {
    const fittingAddonPrice = rpe.HotelAddons[0].HotelAddonsPrices?.find(x => x.PriceType === priceType);
    if (fittingAddonPrice) {
      if (fittingAddonPrice[fieldname] === value) return;
      fittingAddonPrice[fieldname] = value;
      let nonDesignatedPrice = priceToUpdate === "AmountAfterTax" ? "AmountBeforeTax" : "AmountAfterTax";
      fittingAddonPrice[nonDesignatedPrice] = 0;
    }
    else {
      rpe.HotelAddons[0].HotelAddonsPrices.push({
        HotelAddonId: rpe.HotelAddons[0].Id || 0,
        PriceType: priceType,
        [fieldname]: value
      });
    }
    setIsDirty(true);
    rpe.IsUpdated = true;
    setPricingElements([...pricingElements])
  }

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  return (
    <Modal size="large" open={open} dimmer centered className="price-list-settings">
      <Modal.Header>
        {`${text("EditAddonPrices")} - ${rateElementName}`}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths={4}>
            <DatePicker
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("From")}
              onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
              clearable={false}
              value={startDate}
            />
            <DatePicker
              minDate={moment(startDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
              label={text("To")}
              onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
              clearable={false} value={endDate}
            />
            <div className="weekdays-select">
              <WeekDaysSelect onChange={setSelectedDays} dates={memoDatesForWeekComponent} />
              <div className="add-pricing-button">
                <Button primary onClick={addPricingElement} disabled={selectedDays?.length === 0}>{text("Add")}</Button>
              </div>
            </div>
          </Form.Group>
          <Form.Group widths={4}>
            <DatePicker
              label={`${text("FilterBy")} ${text("FromDate")}`}
              value={fromFilterDate}
              onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(state.StartDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <DatePicker
              label={`${text("FilterBy")} ${text("ToDate")}`}
              value={toFilterDate}
              onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
              clearable={false}
              minDate={moment(fromFilterDate).startOf("day").toDate()}
              maxDate={new Date(state.EndDate)}
            />
            <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
              <Button primary onClick={() => {
                setFromFilterDate();
                setToFilterDate();
              }}>{text("Clear")}</Button>
            </div>
            <Form.Field></Form.Field>
          </Form.Group>
          <PriceElementsList state={state} pricings={pricingElements}>
              {filterDateRangesByDateRange(pricingElements, fromFilterDate, toFilterDate)?.filter(x => x.IsActive && !x.IsOverride && !x.IsDummy).map((element) => {
                const isDerived = element.RateId === state.BaseRateId;
                return (
                  <List.Item key={element.Id || element.TempId}>
                    <PricingElementPanel
                      pricing={element}
                      onDelete={() => onDelete(element, pricingElements, setPricingElements)}
                      onSave={onPricingSaveClicked}
                      isDerived={isDerived}
                      onCopy={onCopy}
                      setPricings={setPricingElements}
                    >
                      <List.Content>
                        <AddonEditingTable doesPriceIncludeTax={state.IsProductPricingIncludesTax} isDerived={element.RateId === state.BaseRateId} RatePricingTypeId={element.RatePricingTypeId} priceToUpdate={priceToUpdate} prices={element?.HotelAddons?.[0]?.HotelAddonsPrices} onChange={(priceType, fieldname, value) => changeHotelAddonPrice(element, priceType, fieldname, value)} />
                      </List.Content>
                    </PricingElementPanel>
                  </List.Item>
                );
              })}
          </PriceElementsList>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={() => onSave(true)}>
          {text("Saveandclose")}
        </Button>
        <Button positive onClick={() => onSave()}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PerPersonAddonPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateRPEs: PropTypes.func,
  EntityId: PropTypes.number,
  Packages: PropTypes.array,
  RatePricingTypeId: PropTypes.number,
  rateElementName: PropTypes.string,
  onDelete: PropTypes.func,
  onPricingSave: PropTypes.func
};
